import React, { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { Helper } from '@react-three/drei'

/**
 * ShadowSunLight — the directional shadow-casting "sun" (the caster half of the
 * old ContactShadow). It mainly populates a shadow map; intensity is kept low
 * so the IBL still owns the overall look. Its shadow lands on the shared
 * <ShadowCatcher> plane.
 *
 * Split out from the catcher plane deliberately: mounting the light
 * independently means toggling the directional light removes ONLY its shadow,
 * while the shared plane persists for any other caster (e.g. the spotlight).
 *
 * Shadow-map quality (mapSize/frustum/bias) belongs to THIS light's shadow map,
 * so those knobs live with the directional light in the outliner. The
 * imperative effect mirrors three's requirement that resizing the shadow map
 * disposes the old render targets and moving the ortho frustum re-runs
 * updateProjectionMatrix() — see the ContactShadow history for the mapSize bug.
 *
 * `aim` is the point the light looks at (the head probe — the same origin its
 * placement orbits). It used to be left at three's default target, the world
 * origin, so the light's direction and its orbit disagreed by head height; with
 * a direction gizmo on the sun that difference would show. `selected` draws the
 * DirectionalLightHelper (plane + line to the aim), as Unreal shows a selected
 * directional's arrow.
 */
export default function ShadowSunLight({
  lightPos = [2, 5, 2],
  aim = [0, 0, 0],
  intensity = 0,
  color = '#ffffff',
  mapSize = 1024,
  cameraSize = 3,
  radius = 12,
  blurSamples = 30,
  bias = -0.0005,
  selected = false,
}) {
  const lightRef = useRef(null)
  const target = useMemo(() => new THREE.Object3D(), [])
  const prevMapSizeRef = useRef(mapSize)
  useEffect(() => {
    const light = lightRef.current
    if (!light) return
    const cam = light.shadow.camera
    cam.left = -cameraSize
    cam.right = cameraSize
    cam.top = cameraSize
    cam.bottom = -cameraSize
    cam.updateProjectionMatrix()
    if (prevMapSizeRef.current !== mapSize) {
      prevMapSizeRef.current = mapSize
      light.shadow.mapSize.set(mapSize, mapSize)
      // Drop old target(s) so three re-allocates at the new resolution. VSM
      // needs BOTH map + mapPass disposed or the two-pass blur desyncs.
      if (light.shadow.map) { light.shadow.map.dispose(); light.shadow.map = null }
      if (light.shadow.mapPass) { light.shadow.mapPass.dispose(); light.shadow.mapPass = null }
    }
    light.shadow.bias = bias
  }, [mapSize, cameraSize, bias])

  return (
    <>
      <directionalLight
        ref={lightRef}
        position={lightPos}
        target={target}
        color={color}
        intensity={intensity}
        castShadow
        shadow-mapSize-width={mapSize}
        shadow-mapSize-height={mapSize}
        shadow-camera-near={0.1}
        shadow-camera-far={20}
        shadow-camera-left={-cameraSize}
        shadow-camera-right={cameraSize}
        shadow-camera-top={cameraSize}
        shadow-camera-bottom={-cameraSize}
        shadow-bias={bias}
        shadow-radius={radius}
        shadow-blurSamples={blurSamples}
      >
        {selected && <Helper type={THREE.DirectionalLightHelper} args={[0.5]} />}
      </directionalLight>
      <primitive object={target} position={aim} />
    </>
  )
}
